/**
 * Alpine.js Quick Search Filters Component
 * Handles the discover people filter panel with localStorage persistence
 */

import { lockBodyScroll, unlockBodyScroll } from './scroll-lock.js';

/**
 * Creates a quick search filter component
 * @param {Object} config - Initial configuration
 * @param {number} config.min - Current minimum age
 * @param {number} config.max - Current maximum age
 * @param {number} config.rangeMin - Minimum allowed age
 * @param {number} config.rangeMax - Maximum allowed age
 * @param {boolean} config.male - Male filter enabled
 * @param {boolean} config.female - Female filter enabled
 * @param {string} config.selectedCountry - Initial country code from server (single, for backwards compat)
 */
export function quickSearchFilters(config = {}) {
    return {
        filtersOpen: false,
        min: config.min ?? 16,
        max: config.max ?? 65,
        rangeMin: config.rangeMin ?? 16,
        rangeMax: config.rangeMax ?? 65,
        male: config.male ?? false,
        female: config.female ?? false,
        selectedCountries: [],
        online: false,
        debounceTimer: null,
        storageKey: 'discover-filters',
        translations: config.translations ?? { searchCountries: 'Search countries', anywhere: 'Anywhere' },

        get minPercent() {
            return ((this.min - this.rangeMin) / (this.rangeMax - this.rangeMin)) * 100;
        },

        get maxPercent() {
            return ((this.max - this.rangeMin) / (this.rangeMax - this.rangeMin)) * 100;
        },

        get filterCount() {
            let count = 0;
            if (this.min !== this.rangeMin || this.max !== this.rangeMax) count++;
            if (this.male) count++;
            if (this.female) count++;
            if (this.selectedCountries.length > 0) count++;
            if (this.online) count++;
            return count;
        },

        updateMin(val) {
            this.min = Math.min(parseInt(val), this.max - 1);
        },

        updateMax(val) {
            this.max = Math.max(parseInt(val), this.min + 1);
        },

        formatAge(val) {
            return val >= this.rangeMax ? val + '+' : val;
        },

        clearFilters() {
            this.min = this.rangeMin;
            this.max = this.rangeMax;
            this.male = false;
            this.female = false;
            this.selectedCountries = [];
            this.online = false;
            window.dispatchEvent(new CustomEvent('clear-quick-search-country'));
            this.triggerChange();
        },

        saveFilters() {
            try {
                localStorage.setItem(this.storageKey, JSON.stringify({
                    min: this.min,
                    max: this.max,
                    male: this.male,
                    female: this.female,
                    selectedCountries: this.selectedCountries,
                    online: this.online
                }));
            } catch (e) {
                // localStorage may be unavailable
            }
        },

        loadFilters() {
            try {
                const saved = localStorage.getItem(this.storageKey);
                if (saved) {
                    const data = JSON.parse(saved);
                    if (data.min >= this.rangeMin && data.min <= this.rangeMax) this.min = data.min;
                    if (data.max >= this.rangeMin && data.max <= this.rangeMax) this.max = data.max;
                    if (typeof data.male === 'boolean') this.male = data.male;
                    if (typeof data.female === 'boolean') this.female = data.female;
                    if (typeof data.online === 'boolean') this.online = data.online;

                    // Load countries (supports both new array and legacy single-country format)
                    if (Array.isArray(data.selectedCountries) && data.selectedCountries.length > 0) {
                        this.selectedCountries = data.selectedCountries;
                    } else if (data.selectedCountry && data.selectedCountry !== '---') {
                        this.selectedCountries = [{
                            code: data.selectedCountry,
                            name: data.selectedCountryName || data.selectedCountry
                        }];
                    }
                }
            } catch (e) {
                // localStorage may be unavailable or data corrupted
            }
        },

        triggerChange() {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = setTimeout(() => {
                this.saveFilters();
                this.$refs.triggerInput?.dispatchEvent(new Event('change', { bubbles: true }));
            }, 500);
        },

        /**
         * Sets up touch handling for the slider container.
         * Called via x-init on the slider wrapper element.
         */
        initSliderTouch(containerEl) {
            const rangeInputs = containerEl.querySelectorAll('input[type="range"]');
            if (rangeInputs.length < 2) return;

            const minInput = rangeInputs[0];
            const maxInput = rangeInputs[1];
            let activeInput = null;

            const getValueFromTouch = (touch) => {
                const rect = containerEl.getBoundingClientRect();
                const x = touch.clientX - rect.left;
                const percent = Math.max(0, Math.min(1, x / rect.width));
                return Math.round(this.rangeMin + percent * (this.rangeMax - this.rangeMin));
            };

            const handleTouchStart = (e) => {
                e.preventDefault();
                const touch = e.touches[0];
                const value = getValueFromTouch(touch);

                // Determine which thumb is closer
                const distToMin = Math.abs(value - this.min);
                const distToMax = Math.abs(value - this.max);

                if (distToMin <= distToMax) {
                    activeInput = minInput;
                    const newVal = Math.min(value, this.max - 1);
                    minInput.value = newVal;
                    this.updateMin(newVal);
                } else {
                    activeInput = maxInput;
                    const newVal = Math.max(value, this.min + 1);
                    maxInput.value = newVal;
                    this.updateMax(newVal);
                }
            };

            const handleTouchMove = (e) => {
                if (!activeInput) return;
                e.preventDefault();
                const touch = e.touches[0];
                const value = getValueFromTouch(touch);

                if (activeInput === minInput) {
                    const newVal = Math.min(value, this.max - 1);
                    minInput.value = newVal;
                    this.updateMin(newVal);
                } else {
                    const newVal = Math.max(value, this.min + 1);
                    maxInput.value = newVal;
                    this.updateMax(newVal);
                }
            };

            const handleTouchEnd = () => {
                activeInput = null;
            };

            containerEl.addEventListener('touchstart', handleTouchStart, { passive: false });
            containerEl.addEventListener('touchmove', handleTouchMove, { passive: false });
            containerEl.addEventListener('touchend', handleTouchEnd);
        },

        /**
         * Opens the filter modal and locks body scroll
         */
        openModal() {
            this.filtersOpen = true;
            lockBodyScroll();

            this.$nextTick(() => {
                if (window.LucideIcons) {
                    window.LucideIcons.initialize();
                }
            });
        },

        /**
         * Closes the filter modal and unlocks body scroll
         */
        closeModal() {
            this.filtersOpen = false;
            unlockBodyScroll();
        },

        init() {
            this.loadFilters();

            this.$nextTick(() => this.triggerChange());

            this.$watch('min', () => this.triggerChange());
            this.$watch('max', () => this.triggerChange());
            this.$watch('male', () => this.triggerChange());
            this.$watch('female', () => this.triggerChange());
            this.$watch('online', () => this.triggerChange());
        }
    };
}
